import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { FormValues } from '@features/users/adminPanel/components/Form/Form.types.ts';
import { transformUserProfilePayload } from '@features/users/adminPanel/services/userProfile/service.dto.ts';
import {
  postUserProfileService,
  putUserProfileService,
} from '@features/users/adminPanel/services/userProfile/service.ts';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { SHARED_QUERY_KEYS } from '@shared/consts/queryKeys.ts';

export function useUserProfileMutation() {
  const { t } = useTranslation([i18nNameSpace.API]);
  const queryClient = useQueryClient();

  const refetchUserProfileData = async () =>
    await queryClient.invalidateQueries({ queryKey: [SHARED_QUERY_KEYS.BILLING_INFO] });

  const { mutate: postUserProfileMutation, isPending: userProfileMutationIsPending } = useMutation({
    mutationFn: (payload: FormValues) => postUserProfileService(transformUserProfilePayload(payload)),
    onSuccess: async () => {
      await refetchUserProfileData();
      toast.success(<SuccessMessage message={t('api.success.userProfile.successfullyInitialized')} />);
    },
  });

  const { mutate: putUserProfileMutation, isPending: putUserProfileMutationIsPending } = useMutation({
    mutationFn: ({ payload, id }: { payload: FormValues; id: string }) =>
      putUserProfileService(id, transformUserProfilePayload(payload)),
    onSuccess: async () => {
      await refetchUserProfileData();
      toast.success(<SuccessMessage message={t('api.success.userProfile.successfullyUpdated')} />);
    },
  });

  return {
    refetchUserProfileData,
    postUserProfileMutation,
    userProfileMutationIsPending,
    putUserProfileMutation,
    putUserProfileMutationIsPending,
  };
}
